import React, { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';

export type DialogVariant = 'info' | 'danger' | 'success' | 'error';

interface DialogProps {
  open: boolean;
  title?: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  onConfirm?: () => void;
  onClose: () => void;
}

const VARIANT_META: Record<DialogVariant, { color: string; Icon: React.ComponentType<{ size?: number }>; confirmClass: string }> = {
  info: { color: '#3b82f6', Icon: Info, confirmClass: 'btn btn-primary' },
  danger: { color: '#ef4444', Icon: AlertTriangle, confirmClass: 'btn btn-danger' },
  success: { color: '#10b981', Icon: CheckCircle2, confirmClass: 'btn btn-success' },
  error: { color: '#ef4444', Icon: XCircle, confirmClass: 'btn btn-primary' },
};

const Dialog: React.FC<DialogProps> = ({
  open,
  title,
  message,
  confirmText = 'Aceptar',
  cancelText = 'Cancelar',
  variant = 'info',
  onConfirm,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const meta = VARIANT_META[variant];
  const Icon = meta.Icon;
  const isConfirm = !!onConfirm;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#003554',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 14,
          padding: '32px 28px 24px',
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          textAlign: 'center',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: `${meta.color}22`,
            color: meta.color,
            marginBottom: 16,
          }}
        >
          <Icon size={28} />
        </span>
        {title && (
          <h3 style={{ margin: 0, marginBottom: 8, color: '#fff', fontSize: '1.15rem' }}>
            {title}
          </h3>
        )}
        <div
          style={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: '0.95rem',
            lineHeight: 1.5,
            marginBottom: 24,
          }}
        >
          {message}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'center',
          }}
        >
          {isConfirm && (
            <button className="btn btn-secondary" onClick={onClose} style={{ minWidth: 120 }}>
              {cancelText}
            </button>
          )}
          <button
            className={meta.confirmClass}
            onClick={() => {
              if (onConfirm) onConfirm();
              onClose();
            }}
            style={{ minWidth: 120 }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dialog;
